import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
} from "react";

import { useNavigate } from "react-router-dom";

import {
    Search,
    MapPin,
    BriefcaseBusiness,
    Download,
    UserRound,
    SlidersHorizontal,
    X,
    ChevronRight,
    Award,
    ChevronLeft,
    ChevronDown,
    Sparkles,
    ArrowUpRight,
    CheckCircle2,
    Users,
    Building2,
    Clock3,
    ShieldCheck,
    Zap,
    Target,
    GraduationCap,
    Code2,
    Brain,
    Cloud,
    Mail,
    Phone,
    FileSearch,
    Handshake,
    Rocket,
    ClipboardList,
} from "lucide-react";

import { getAllTrainer } from "../services/trainerService";

import {
    getTrainerProfile,
    getProfilePictureUrl,
} from "../services/trainerProfileService";

import "./Home.css";
import Banner from "../asset/Banner.jpeg";


const ITEMS_PER_PAGE = 8;


const techCategories = [
    {
        title: "Software Development",
        subtitle: "Build & Code",
        icon: Code2,
        skills: ["Java", "Python", "C++", "MERN"],
    },
    {
        title: "AI & Data",
        subtitle: "Data Intelligence",
        icon: Brain,
        skills: [
            "Data Science",
            "AIML",
            "GenAI",
            "Power BI",
        ],
    },
    {
        title: "Cloud & DevOps",
        subtitle: "Scale Faster",
        icon: Cloud,
        skills: [
            "AWS",
            "Azure",
            "DevOps",
            "Linux",
        ],
    },
];


const processSteps = [
    {
        icon: FileSearch,
        title: "Search & Filter",
        description:
            "Browse verified trainer profiles by technology, location and commercial rate.",
    },
    {
        icon: ClipboardList,
        title: "Compare Profiles",
        description:
            "Review skills, experience and education to shortlist the right fit.",
    },
    {
        icon: Handshake,
        title: "Connect Directly",
        description:
            "Download resumes and reach out to trainers who match your requirement.",
    },
    {
        icon: Rocket,
        title: "Start Training",
        description:
            "Finalize the engagement and kick off your training program with confidence.",
    },
];


const testimonials = [
    {
        quote:
            "CertED helped us connect with relevant trainers quickly and reduced the time spent on sourcing.",
        name: "Training Partner",
        role: "Corporate Learning Team",
    },
    {
        quote:
            "The platform makes it easy to showcase my expertise and discover relevant training opportunities.",
        name: "Technical Trainer",
        role: "Independent Trainer",
    },
    {
        quote:
            "The quality of trainer profiles and the speed of coordination made the process much easier.",
        name: "Academic Partner",
        role: "Institutional Training Team",
    },
];


function Home() {

    const navigate = useNavigate();


    // =========================================================
    // TRAINERS
    // =========================================================

    const [trainers, setTrainers] = useState([]);
    const [loading, setLoading] = useState(true);


    // =========================================================
    // SEARCH / FILTERS
    // =========================================================

    const [search, setSearch] = useState("");
    const [technology, setTechnology] = useState("");
    const [location, setLocation] = useState("");
    const [rate, setRate] = useState("");


    // =========================================================
    // PAGINATION / UI
    // =========================================================

    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [testimonialIndex, setTestimonialIndex] =
        useState(0);

    // Remounting the testimonial content on change lets
    // Home.css's own "certedReveal" mount-animation replay
    // naturally, with no extra CSS needed.
    const [testimonialAnimKey, setTestimonialAnimKey] =
        useState(0);


    // =========================================================
    // PROFILE MODAL
    // =========================================================

    const [selectedTrainer, setSelectedTrainer] =
        useState(null);

    const [selectedTrainerProfile, setSelectedTrainerProfile] =
        useState(null);

    const [profileLoading, setProfileLoading] =
        useState(false);

    const [profileImage, setProfileImage] =
        useState(null);


    // =========================================================
    // TRAINER CARD PROFILE IMAGES
    // =========================================================

    const [trainerImages, setTrainerImages] =
        useState({});

    const trainerImageUrlsRef = useRef({});


    // =========================================================
    // LOAD ALL TRAINERS
    // =========================================================

    useEffect(() => {
        loadTrainers();
    }, []);


    const loadTrainers = async () => {

        try {

            setLoading(true);

            const response = await getAllTrainer();

            let data = [];

            if (Array.isArray(response)) {

                data = response;

            } else if (Array.isArray(response?.data)) {

                data = response.data;

            } else if (Array.isArray(response?.content)) {

                data = response.content;

            }

            setTrainers(data);

        } catch (error) {

            console.error(
                "Unable to load trainers:",
                error
            );

            setTrainers([]);

        } finally {

            setLoading(false);

        }
    };


    // =========================================================
    // BASIC HELPERS
    // =========================================================

    const safeString = (value) => {

        if (
            value === null ||
            value === undefined
        ) {
            return "";
        }

        return String(value);
    };


    const getInitials = (name) => {

        const cleanName =
            safeString(name).trim();

        if (!cleanName) {
            return "TR";
        }

        return (
            cleanName
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(
                    (word) =>
                        word
                            .charAt(0)
                            .toUpperCase()
                )
                .join("") || "TR"
        );
    };


    const normalizeSkills = (skills) => {

        if (Array.isArray(skills)) {

            return skills
                .map(
                    (skill) =>
                        safeString(skill).trim()
                )
                .filter(Boolean);
        }


        if (typeof skills === "string") {

            return skills
                .split(",")
                .map(
                    (skill) => skill.trim()
                )
                .filter(Boolean);
        }


        return [];
    };


    const getTrainerRate = (trainer) => {

        const value =
            trainer?.rate ??
            trainer?.commercialRate ??
            trainer?.expectedRate ??
            trainer?.price ??
            0;

        const number = Number(
            String(value).replace(
                /[^\d.]/g,
                ""
            )
        );

        return Number.isFinite(number)
            ? number
            : 0;
    };


    const getExperience = (trainer) => {

        return safeString(
            trainer?.experience ??
                trainer?.yearsOfExperience ??
                trainer?.exp ??
                ""
        ).trim();
    };


    const getLocation = (trainer) => {

        const city =
            safeString(
                trainer?.city
            ).trim();

        const state =
            safeString(
                trainer?.state
            ).trim();


        if (city && state) {

            return `${city}, ${state}`;

        }


        return (
            city ||
            state ||
            "Location not specified"
        );
    };


    // =========================================================
    // FILTER TRAINERS
    // =========================================================

    const filteredTrainers = useMemo(() => {

        const query =
            search
                .trim()
                .toLowerCase();


        return trainers.filter(
            (trainer) => {

                const trainerName =
                    safeString(
                        trainer?.name ||
                        trainer?.fullName
                    ).toLowerCase();


                const trainerEmail =
                    safeString(
                        trainer?.email
                    ).toLowerCase();


                const trainerLocation =
                    getLocation(
                        trainer
                    ).toLowerCase();


                const trainerSkills =
                    normalizeSkills(
                        trainer?.skills
                    )
                        .join(" ")
                        .toLowerCase();


                const matchesSearch =
                    !query ||
                    trainerName.includes(query) ||
                    trainerEmail.includes(query) ||
                    trainerLocation.includes(query) ||
                    trainerSkills.includes(query);


                const matchesTechnology =
                    !technology ||
                    trainerSkills.includes(
                        technology.toLowerCase()
                    );


                const matchesLocation =
                    !location ||
                    trainerLocation.includes(
                        location.toLowerCase()
                    );


                const trainerRate =
                    getTrainerRate(
                        trainer
                    );


                const matchesRate =
                    !rate ||
                    (
                        rate === "under-1000" &&
                        trainerRate > 0 &&
                        trainerRate < 1000
                    ) ||
                    (
                        rate === "1000-2000" &&
                        trainerRate >= 1000 &&
                        trainerRate <= 2000
                    ) ||
                    (
                        rate === "2000-plus" &&
                        trainerRate > 2000
                    );


                return (
                    matchesSearch &&
                    matchesTechnology &&
                    matchesLocation &&
                    matchesRate
                );
            }
        );

    }, [
        trainers,
        search,
        technology,
        location,
        rate,
    ]);


    // =========================================================
    // PAGINATION
    // =========================================================

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredTrainers.length /
                ITEMS_PER_PAGE
        )
    );


    const visibleTrainers = useMemo(() => {

        const start =
            (currentPage - 1) *
            ITEMS_PER_PAGE;


        return filteredTrainers.slice(
            start,
            start + ITEMS_PER_PAGE
        );

    }, [
        filteredTrainers,
        currentPage,
    ]);


    useEffect(() => {

        setCurrentPage(1);

    }, [
        search,
        technology,
        location,
        rate,
    ]);


    useEffect(() => {

        if (
            currentPage >
            totalPages
        ) {
            setCurrentPage(
                totalPages
            );
        }

    }, [
        currentPage,
        totalPages,
    ]);


    // =========================================================
    // FETCH PROFILE IMAGES FOR VISIBLE TRAINERS
    // =========================================================

    useEffect(() => {

        if (
            !visibleTrainers ||
            visibleTrainers.length === 0
        ) {
            return;
        }


        let cancelled = false;


        const loadTrainerImages = async () => {

            const token =
                localStorage.getItem(
                    "token"
                );


            for (
                const trainer
                of visibleTrainers
            ) {

                const trainerId =
                    trainer?.id;


                if (!trainerId) {
                    continue;
                }


                if (
                    trainerImageUrlsRef
                        .current[
                            trainerId
                        ]
                ) {
                    continue;
                }


                try {

                    const imageUrl =
                        getProfilePictureUrl(
                            trainerId
                        );


                    const response =
                        await fetch(
                            `${imageUrl}?t=${Date.now()}`,
                            {
                                headers: {
                                    ...(token
                                        ? {
                                            Authorization:
                                                `Bearer ${token}`,
                                        }
                                        : {}),
                                },
                            }
                        );


                    if (
                        !response.ok
                    ) {
                        continue;
                    }


                    const blob =
                        await response.blob();


                    if (
                        !blob ||
                        blob.size === 0
                    ) {
                        continue;
                    }


                    const objectUrl =
                        URL.createObjectURL(
                            blob
                        );


                    if (
                        cancelled
                    ) {

                        URL.revokeObjectURL(
                            objectUrl
                        );

                        continue;
                    }


                    trainerImageUrlsRef
                        .current[
                            trainerId
                        ] = objectUrl;


                    setTrainerImages(
                        (previous) => ({
                            ...previous,
                            [trainerId]:
                                objectUrl,
                        })
                    );

                } catch (error) {

                    console.warn(
                        `Unable to load profile image for trainer ${trainerId}:`,
                        error
                    );

                }
            }
        };


        loadTrainerImages();


        return () => {

            cancelled = true;

        };

    }, [visibleTrainers]);


    // =========================================================
    // CLEAN UP ALL TRAINER IMAGE OBJECT URLS
    // =========================================================

    useEffect(() => {

        return () => {

            Object.values(
                trainerImageUrlsRef.current
            ).forEach(
                (url) => {

                    try {

                        URL.revokeObjectURL(
                            url
                        );

                    } catch (error) {

                        // Ignore cleanup errors

                    }
                }
            );


            trainerImageUrlsRef.current =
                {};

        };

    }, []);


    // =========================================================
    // FILTER HELPERS
    // =========================================================

    const clearFilters = () => {

        setSearch("");
        setTechnology("");
        setLocation("");
        setRate("");
        setCurrentPage(1);

    };


    const hasFilters = Boolean(
        search ||
        technology ||
        location ||
        rate
    );


    // =========================================================
    // RESUME DOWNLOAD
    // =========================================================

    const downloadResume = (trainer) => {

        if (!trainer?.id) {
            return;
        }


        const resumeUrl =
            `/api/trainers/resume/${trainer.id}`;


        window.open(
            resumeUrl,
            "_blank",
            "noopener,noreferrer"
        );

    };


    // =========================================================
    // VIEW TRAINER PROFILE
    // =========================================================

    const viewProfile = async (trainer) => {

        if (!trainer?.id) {
            return;
        }


        setSelectedTrainer(trainer);
        setSelectedTrainerProfile(null);
        setProfileImage(null);
        setProfileLoading(true);


        try {

            const response =
                await getTrainerProfile(
                    trainer.id
                );


            const profileData =
                response?.data ||
                response ||
                {};


            setSelectedTrainerProfile(
                profileData
            );


            try {

                const imageUrl =
                    getProfilePictureUrl(
                        trainer.id
                    );


                const token =
                    localStorage.getItem(
                        "token"
                    );


                const imageResponse =
                    await fetch(
                        `${imageUrl}?t=${Date.now()}`,
                        {
                            headers: {
                                ...(token
                                    ? {
                                        Authorization:
                                            `Bearer ${token}`,
                                    }
                                    : {}),
                            },
                        }
                    );


                if (
                    imageResponse.ok
                ) {

                    const blob =
                        await imageResponse.blob();


                    if (
                        blob &&
                        blob.size > 0
                    ) {

                        const objectUrl =
                            URL.createObjectURL(
                                blob
                            );


                        setProfileImage(
                            objectUrl
                        );
                    }
                }

            } catch (imageError) {

                console.warn(
                    "Unable to load trainer profile picture:",
                    imageError
                );

            }

        } catch (error) {

            console.error(
                "Unable to load trainer profile:",
                error
            );


            setSelectedTrainerProfile({
                trainer,
                details: {},
                education: [],
                experience: [],
            });

        } finally {

            setProfileLoading(
                false
            );

        }
    };


    // =========================================================
    // CLOSE PROFILE MODAL
    // =========================================================

    const closeProfileModal = () => {

        if (profileImage) {

            try {

                URL.revokeObjectURL(
                    profileImage
                );

            } catch (error) {

                // Ignore object URL cleanup errors

            }
        }


        setProfileImage(null);
        setSelectedTrainer(null);
        setSelectedTrainerProfile(null);
        setProfileLoading(false);

    };


    // =========================================================
    // NAVIGATION
    // =========================================================

    const scrollToDirectory = () => {

        document
            .getElementById(
                "trainer-directory"
            )
            ?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });

    };


    const goToJobs = () =>
        navigate("/jobs");


    const goToTrainerRegister = () =>
        navigate("/trainer-register");


    const goToPartnerRegister = () =>
        navigate("/partner-register");


    // =========================================================
    // TESTIMONIALS
    // =========================================================

    const nextTestimonial = useCallback(() => {

        setTestimonialIndex(
            (previous) =>
                (
                    previous + 1
                ) %
                testimonials.length
        );

        setTestimonialAnimKey((k) => k + 1);

    }, []);


    const previousTestimonial = () => {

        setTestimonialIndex(
            (previous) =>
                (
                    previous -
                    1 +
                    testimonials.length
                ) %
                testimonials.length
        );

        setTestimonialAnimKey((k) => k + 1);

    };


    // Auto-rotate testimonials for a livelier section.
    // Home.css already animates .testimonial-content on
    // mount ("certedReveal"), so remounting it via the key
    // below replays that fade-in automatically.
    useEffect(() => {

        const interval = setInterval(() => {
            nextTestimonial();
        }, 6000);

        return () => clearInterval(interval);

    }, [nextTestimonial]);


    // =========================================================
    // STATS
    // =========================================================

    const stats = [

        {
            value:
                `${trainers.length || 100}+`,
            label:
                "Trainer Profiles",
            icon: Users,
        },

        {
            value: "50+",
            label:
                "Skill Categories",
            icon: Award,
        },

        {
            value:
                "Pan India",
            label:
                "Training Reach",
            icon: MapPin,
        },

        {
            value:
                "24/7",
            label:
                "Opportunity Discovery",
            icon: Zap,
        },

    ];


    // =========================================================
    // SELECTED TRAINER — DERIVED RESUME DATA
    // =========================================================

    const resumeSkills = selectedTrainer
        ? normalizeSkills(selectedTrainer?.skills)
        : [];

    const resumeEducation = Array.isArray(
        selectedTrainerProfile?.education
    )
        ? selectedTrainerProfile.education
        : [];

    const resumeExperience = Array.isArray(
        selectedTrainerProfile?.experience
    )
        ? selectedTrainerProfile.experience
        : [];

    const resumeSummary = safeString(
        selectedTrainerProfile?.summary ||
        selectedTrainerProfile?.bio ||
        selectedTrainerProfile?.about ||
        ""
    ).trim();


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <main className="home-page">


            {/* =================================================
                HERO SECTION
            ================================================= */}

            <section className="hero-section">

                <div className="hero-bg-grid" />

                <div className="hero-glow glow-one" />
                <div className="hero-glow glow-two" />

                <div className="hero-container">

                    <div className="hero-content">

                        <div className="hero-badge">
                            <span />
                            India's Trainer Marketplace
                        </div>

                        <h1>
                            Connect with
                            {" "}
                            <span>Expert Trainers</span>
                        </h1>

                        <p>
                            Discover skilled trainers across
                            technology, business, data, cloud
                            and more. Build the right training
                            team for your organization.
                        </p>

                        <div className="hero-buttons">

                            <button
                                className="hero-primary"
                                onClick={scrollToDirectory}
                            >
                                <Search size={16} />
                                Find Trainers
                                <ArrowUpRight size={16} />
                            </button>

                            <button
                                className="hero-secondary"
                                onClick={goToTrainerRegister}
                            >
                                Become a Trainer
                                <ChevronRight size={16} />
                            </button>

                        </div>

                        <div className="hero-mini-proof">

                            <div className="proof-stack">
                                <span>CE</span>
                                <span>RT</span>
                                <span>ED</span>
                                <span>+</span>
                            </div>

                            <div>
                                <strong>
                                    {trainers.length || 100}+ trainers onboard
                                </strong>
                                <small>
                                    Verified profiles across India
                                </small>
                            </div>

                        </div>

                    </div>

                    <div className="hero-visual">

                        <div className="hero-circle circle-one" />
                        <div className="hero-circle circle-two" />

                        <div className="hero-image-card">

                            <img
                                src={Banner}
                                alt="CertED Technologies"
                            />

                            <div className="image-overlay" />

                            <div className="image-caption">
                                <span>TRAINER MARKETPLACE</span>
                                <strong>
                                    Building skilled teams,
                                    one trainer at a time.
                                </strong>
                            </div>

                        </div>

                        <div className="floating-card floating-top">

                            <div className="floating-icon">
                                <ShieldCheck size={17} />
                            </div>

                            <div>
                                <strong>Verified</strong>
                                <span>Trainer profiles</span>
                            </div>

                        </div>

                        <div className="floating-card floating-bottom">

                            <Zap size={17} />

                            <div>
                                <strong>Fast Matching</strong>
                                <span>Skill based search</span>
                            </div>

                        </div>

                    </div>

                </div>

              
            </section>


            {/* =================================================
                STATS SECTION
            ================================================= */}

            <section className="stats-section">

                <div className="stats-container">

                    {stats.map(
                        (stat, index) => {

                            const Icon = stat.icon;

                            return (

                                <div
                                    className="stat-box"
                                    key={index}
                                >

                                    <div className="stat-icon">
                                        <Icon size={20} />
                                    </div>

                                    <div>
                                        <strong>{stat.value}</strong>
                                        <span>{stat.label}</span>
                                    </div>

                                </div>

                            );

                        }
                    )}

                </div>

            </section>


            {/* =================================================
                EXPERTISE / TECH CATEGORIES
            ================================================= */}

            <section className="expertise-section">

                <div className="section-shell">

                    <div className="section-title">

                        <div>
                            <span>EXPLORE EXPERTISE</span>
                            <h2>
                                Find Trainers by <b>Technology</b>
                            </h2>
                        </div>

                        <p>
                            Handpicked skill categories to help you
                            quickly zero in on the right expertise.
                        </p>

                    </div>

                    <div className="expertise-grid">

                        {techCategories.map(
                            (category, index) => {

                                const Icon = category.icon;

                                return (

                                    <div
                                        className="expertise-card"
                                        key={category.title}
                                        onClick={() => {
                                            setTechnology(
                                                category.skills[0]
                                            );
                                            scrollToDirectory();
                                        }}
                                    >

                                        <span className="expertise-number">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>

                                        <div className="expertise-icon">
                                            <Icon size={24} />
                                        </div>

                                        <span>{category.subtitle}</span>

                                        <h3>{category.title}</h3>

                                        <div className="expertise-skills">
                                            {category.skills.map(
                                                (skill) => (
                                                    <span key={skill}>
                                                        {skill}
                                                    </span>
                                                )
                                            )}
                                        </div>

                                        <div className="expertise-arrow">
                                            <ArrowUpRight size={17} />
                                        </div>

                                    </div>

                                );

                            }
                        )}

                    </div>

                </div>

            </section>


            {/* =================================================
                NETWORK / PARTNER BANNER
            ================================================= */}

            <section className="network-section">

                <div className="network-container">

                    <div className="network-content">

                        <span>FOR ORGANIZATIONS</span>

                        <h2>
                            Build a training team with{" "}
                            <b>Pan India reach</b>
                        </h2>

                        <p>
                            Post your requirement once and get matched
                            with verified trainers across technology,
                            location and budget — no more endless
                            sourcing calls.
                        </p>

                        <button onClick={goToPartnerRegister}>
                            <Building2 size={16} />
                            Register as Partner
                            <ArrowUpRight size={16} />
                        </button>

                    </div>

                    <div className="network-image">

                        <img
                            src={Banner}
                            alt="Training network"
                        />

                        <div className="network-image-overlay" />

                        <div className="network-stat-card">
                            <strong>{trainers.length || 100}+</strong>
                            <span>Active trainer network</span>
                        </div>

                    </div>

                </div>

            </section>


            {/* =================================================
                TRAINER DIRECTORY
            ================================================= */}

            <section
                className="directory-section"
                id="trainer-directory"
            >

                <div className="section-shell">

                    <div className="directory-title">

                        <div>
                            <span>TRAINER DIRECTORY</span>
                            <h2>
                                Discover Expert <b>Trainers</b>
                            </h2>
                        </div>

                        <div className="profile-count">
                            <Users size={18} />
                            <div>
                                <strong>{filteredTrainers.length}</strong>
                                <span>Trainers found</span>
                            </div>
                        </div>

                    </div>

                    <div className="directory-toolbar">

                        <div className="directory-search">
                            <Search size={16} />
                            <input
                                type="text"
                                placeholder="Search trainers, skills or location..."
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch("")}
                                    aria-label="Clear search"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <button
                            className="filter-button"
                            onClick={() =>
                                setShowFilters(!showFilters)
                            }
                        >
                            <SlidersHorizontal size={16} />
                            Filters
                            <ChevronDown
                                className="chevron-up"
                                size={15}
                                style={{
                                    transform: showFilters
                                        ? "rotate(180deg)"
                                        : "none",
                                }}
                            />
                        </button>

                    </div>

                    {showFilters && (

                        <div className="advanced-filters">

                            <div>
                                <label>Technology</label>
                                <select
                                    value={technology}
                                    onChange={(e) =>
                                        setTechnology(e.target.value)
                                    }
                                >
                                    <option value="">All Technologies</option>
                                    <option value="Java">Java</option>
                                    <option value="Python">Python</option>
                                    <option value="MERN">MERN</option>
                                    <option value="C++">C++</option>
                                    <option value="Data Science">Data Science</option>
                                    <option value="AIML">AIML</option>
                                    <option value="GenAI">GenAI</option>
                                    <option value="Power BI">Power BI</option>
                                    <option value="AWS">AWS</option>
                                    <option value="Azure">Azure</option>
                                    <option value="DevOps">DevOps</option>
                                    <option value="Linux">Linux</option>
                                </select>
                            </div>

                            <div>
                                <label>Location</label>
                                <div className="filter-location">
                                    <MapPin size={14} />
                                    <input
                                        type="text"
                                        placeholder="City or state"
                                        value={location}
                                        onChange={(e) =>
                                            setLocation(e.target.value)
                                        }
                                    />
                                </div>
                            </div>

                            <div>
                                <label>Commercial Rate</label>
                                <select
                                    value={rate}
                                    onChange={(e) =>
                                        setRate(e.target.value)
                                    }
                                >
                                    <option value="">Any Rate</option>
                                    <option value="under-1000">Under ₹1,000</option>
                                    <option value="1000-2000">₹1,000 – ₹2,000</option>
                                    <option value="2000-plus">₹2,000+</option>
                                </select>
                            </div>

                            <div>
                                <label>&nbsp;</label>
                                <button
                                    className="reset-button"
                                    onClick={clearFilters}
                                    disabled={!hasFilters}
                                >
                                    Reset Filters
                                </button>
                            </div>

                        </div>

                    )}

                    {loading ? (

                        <div className="trainer-grid">
                            {Array.from({ length: 4 }).map(
                                (_, index) => (
                                    <div
                                        className="trainer-skeleton"
                                        key={index}
                                    />
                                )
                            )}
                        </div>

                    ) : filteredTrainers.length === 0 ? (

                        <div className="empty-state">

                            <UserRound />

                            <h3>No trainers found</h3>

                            <p>
                                Try changing your search or filters
                                to see more trainer profiles.
                            </p>

                            {hasFilters && (
                                <button
                                    className="reset-button"
                                    onClick={clearFilters}
                                >
                                    Clear Filters
                                </button>
                            )}

                        </div>

                    ) : (

                        <div className="trainer-grid">

                            {visibleTrainers.map(
                                (trainer, index) => {

                                    const trainerName =
                                        safeString(
                                            trainer?.name ||
                                            trainer?.fullName ||
                                            "Trainer"
                                        ).trim();

                                    const skills =
                                        normalizeSkills(
                                            trainer?.skills
                                        );

                                    const experience =
                                        getExperience(trainer);

                                    const trainerLocation =
                                        getLocation(trainer);

                                    const trainerRate =
                                        getTrainerRate(trainer);

                                    const image =
                                        trainerImages[trainer?.id];

                                    const serial =
                                        (currentPage - 1) *
                                            ITEMS_PER_PAGE +
                                        index +
                                        1;

                                    return (

                                        <article
                                            className="trainer-card"
                                            key={trainer?.id}
                                        >

                                            <span className="trainer-card-number">
                                                {serial}
                                            </span>

                                            <div className="trainer-head">

                                                <div className="trainer-avatar">
                                                    {image ? (
                                                        <img
                                                            src={image}
                                                            alt={trainerName}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                borderRadius: "inherit",
                                                            }}
                                                            onError={() => {
                                                                setTrainerImages(
                                                                    (previous) => {
                                                                        const next = {
                                                                            ...previous,
                                                                        };
                                                                        delete next[trainer.id];
                                                                        return next;
                                                                    }
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        getInitials(trainerName)
                                                    )}
                                                </div>

                                                <div className="trainer-info">

                                                    <h3>{trainerName}</h3>

                                                    <p>
                                                        {safeString(
                                                            trainer?.designation ||
                                                            trainer?.title ||
                                                            "Professional Trainer"
                                                        )}
                                                    </p>

                                                    <p>
                                                        {trainerLocation}
                                                        {experience
                                                            ? ` • ${experience}`
                                                            : ""}
                                                    </p>

                                                </div>

                                            </div>

                                            <div className="trainer-skills">

                                                {skills.length > 0 ? (

                                                    <>
                                                        {skills.slice(0, 4).map(
                                                            (skill) => (
                                                                <span key={skill}>
                                                                    {skill}
                                                                </span>
                                                            )
                                                        )}

                                                        {skills.length > 4 && (
                                                            <span>
                                                                +{skills.length - 4}
                                                            </span>
                                                        )}
                                                    </>

                                                ) : (

                                                    <span>
                                                        Skills not listed
                                                    </span>

                                                )}

                                            </div>

                                            <div className="trainer-footer">

                                                <div className="trainer-rate">
                                                    <small>Rate</small>
                                                    <strong>
                                                        {trainerRate > 0
                                                            ? `₹${trainerRate}/day`
                                                            : "On request"}
                                                    </strong>
                                                </div>

                                                <div className="trainer-actions">

                                                    <button
                                                        title="Download Resume"
                                                        onClick={() =>
                                                            downloadResume(trainer)
                                                        }
                                                    >
                                                        <Download size={15} />
                                                    </button>

                                                    <button
                                                        className="profile-btn"
                                                        onClick={() =>
                                                            viewProfile(trainer)
                                                        }
                                                    >
                                                        View Profile
                                                    </button>

                                                </div>

                                            </div>

                                        </article>

                                    );

                                }
                            )}

                        </div>

                    )}

                    {!loading &&
                        filteredTrainers.length > 0 && (

                            <div className="pagination">

                                <button
                                    disabled={currentPage === 1}
                                    onClick={() =>
                                        setCurrentPage((previous) =>
                                            Math.max(1, previous - 1)
                                        )
                                    }
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft size={15} />
                                </button>

                                {Array.from(
                                    { length: totalPages },
                                    (_, index) => index + 1
                                ).map((page) => (
                                    <button
                                        key={page}
                                        className={
                                            currentPage === page
                                                ? "active"
                                                : ""
                                        }
                                        onClick={() =>
                                            setCurrentPage(page)
                                        }
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    disabled={
                                        currentPage === totalPages
                                    }
                                    onClick={() =>
                                        setCurrentPage((previous) =>
                                            Math.min(
                                                totalPages,
                                                previous + 1
                                            )
                                        )
                                    }
                                    aria-label="Next page"
                                >
                                    <ChevronRight size={15} />
                                </button>

                            </div>

                        )}

                </div>

            </section>


            {/* =================================================
                PROCESS SECTION — HOW IT WORKS
            ================================================= */}

            <section className="process-section">

                <div className="process-title">
                    <h2>How CertED Works</h2>
                    <p>
                        A simple, four-step path from search to
                        a confirmed training engagement.
                    </p>
                </div>

                <div className="process-grid">

                    {processSteps.map((step, index) => {

                        const Icon = step.icon;

                        return (

                            <div
                                className="process-card"
                                key={step.title}
                            >
                                <span>{index + 1}</span>

                                <Icon size={26} />

                                <h3>{step.title}</h3>
                                <p>{step.description}</p>
                            </div>

                        );

                    })}

                </div>

            </section>


            {/* =================================================
                FINAL CTA
            ================================================= */}

            <section className="final-cta">

                <div className="cta-background">
                    <span style={{ top: "18%", left: "12%" }} />
                    <span style={{ top: "62%", left: "78%" }} />
                    <span style={{ top: "80%", left: "30%" }} />
                </div>

                <div className="cta-content">

                    <h2>Ready to Find the Right Trainer?</h2>

                    <p>
                        Explore expert trainers, compare skills
                        and experience, and connect with the right
                        training professional for your requirement.
                    </p>

                    <div>

                        <button onClick={scrollToDirectory}>
                            <Search size={16} />
                            Find a Trainer
                        </button>

                        <button onClick={goToPartnerRegister}>
                            <Building2 size={16} />
                            Post Requirement
                        </button>

                    </div>

                </div>

            </section>


            {/* =================================================
                TESTIMONIALS
            ================================================= */}

            <section className="testimonial-section">

                <div className="testimonial-box">

                    <div className="testimonial-left">

                        <h2>What Our Community Says</h2>

                        <p>
                            Feedback from trainers and organizations
                            who found the right match through CertED.
                        </p>

                    </div>
                    <div
                        className="testimonial-content"
                        key={testimonialAnimKey}
                    >
                        <blockquote>
                            {testimonials[testimonialIndex].quote}
                        </blockquote>
                        <strong>
                            {testimonials[testimonialIndex].name}
                        </strong>
                        <span>
                            {testimonials[testimonialIndex].role}
                        </span>
                        <button
                            onClick={previousTestimonial}
                            aria-label="Previous testimonial"
                        >
                            <ChevronLeft size={15} />
                        </button>
                        <button
                            onClick={nextTestimonial}
                            aria-label="Next testimonial"
                        >
                            <ChevronRight size={15} />
                        </button>
                    </div>
                </div>
            </section>

            {/* =================================================
                TRAINER PROFILE / RESUME MODAL
            ================================================= */}

            {selectedTrainer && (

                <div
                    className="trainer-profile-modal-overlay"
                    onClick={closeProfileModal}
                >

                    <div
                        className="trainer-profile-modal"
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                    >

                        <div className="trainer-profile-modal-header">

                            <div>
                                <span className="resume-modal-kicker">
                                    TRAINER PROFILE
                                </span>
                                <h2>
                                    {safeString(
                                        selectedTrainer?.name ||
                                        selectedTrainer?.fullName ||
                                        "Trainer"
                                    )}
                                </h2>
                            </div>

                            <button
                                className="trainer-profile-close"
                                onClick={closeProfileModal}
                                aria-label="Close profile"
                            >
                                <X size={19} />
                            </button>

                        </div>

                        {profileLoading ? (

                            <div className="resume-modal-loading">
                                <div className="resume-spinner" />
                                <h3>Loading profile</h3>
                                <p>
                                    Fetching trainer details, please wait...
                                </p>
                            </div>

                        ) : (

                            <div className="resume-document">

                                <div className="resume-header">

                                    {profileImage ? (

                                        <div className="resume-profile-photo-wrap resume-avatar-large">
                                            <img
                                                className="resume-profile-photo"
                                                src={profileImage}
                                                alt={safeString(
                                                    selectedTrainer?.name ||
                                                    selectedTrainer?.fullName ||
                                                    "Trainer"
                                                )}
                                                onError={() => {
                                                    if (profileImage) {
                                                        try {
                                                            URL.revokeObjectURL(
                                                                profileImage
                                                            );
                                                        } catch (error) {
                                                            // Ignore cleanup errors
                                                        }
                                                    }
                                                    setProfileImage(null);
                                                }}
                                            />
                                        </div>

                                    ) : (

                                        <div className="resume-avatar-large">
                                            {getInitials(
                                                selectedTrainer?.name ||
                                                selectedTrainer?.fullName
                                            )}
                                        </div>

                                    )}

                                    <div className="resume-header-main">

                                        <h1>
                                            {safeString(
                                                selectedTrainer?.name ||
                                                selectedTrainer?.fullName ||
                                                "Trainer"
                                            )}
                                        </h1>

                                        <h3>
                                            {safeString(
                                                selectedTrainer?.designation ||
                                                selectedTrainer?.title ||
                                                "Professional Trainer"
                                            )}
                                        </h3>

                                        <div className="resume-contact-grid">

                                            <span>
                                                <MapPin
                                                    size={13}
                                                    style={{
                                                        verticalAlign: "-2px",
                                                        marginRight: "4px",
                                                    }}
                                                />
                                                {getLocation(selectedTrainer)}
                                            </span>

                                            {selectedTrainer?.email && (
                                                <span>
                                                    <Mail
                                                        size={13}
                                                        style={{
                                                            verticalAlign: "-2px",
                                                            marginRight: "4px",
                                                        }}
                                                    />
                                                    {safeString(selectedTrainer.email)}
                                                </span>
                                            )}

                                            {(selectedTrainer?.phone ||
                                                selectedTrainer?.mobile) && (
                                                <span>
                                                    <Phone
                                                        size={13}
                                                        style={{
                                                            verticalAlign: "-2px",
                                                            marginRight: "4px",
                                                        }}
                                                    />
                                                    {safeString(
                                                        selectedTrainer.phone ||
                                                        selectedTrainer.mobile
                                                    )}
                                                </span>
                                            )}

                                        </div>

                                    </div>

                                    {selectedTrainer?.status === "APPROVED" && (

                                        <span className="resume-status">
                                            <CheckCircle2 size={14} />
                                            Verified
                                        </span>

                                    )}

                                </div>

                                <div className="resume-body">

                                    <div className="resume-main-column">

                                        <div className="resume-section">

                                            <div className="resume-section-title">
                                                <span>01</span>
                                                <h3>Professional Summary</h3>
                                            </div>

                                            {resumeSummary ? (
                                                <p className="resume-summary">
                                                    {resumeSummary}
                                                </p>
                                            ) : (
                                                <p className="resume-empty-section">
                                                    This trainer has not added
                                                    a professional summary yet.
                                                </p>
                                            )}

                                        </div>

                                        <div className="resume-section">

                                            <div className="resume-section-title">
                                                <span>02</span>
                                                <h3>Experience</h3>
                                            </div>

                                            {resumeExperience.length > 0 ? (

                                                <div className="resume-timeline">

                                                    {resumeExperience.map(
                                                        (exp, index) => (

                                                            <div
                                                                className="resume-timeline-item"
                                                                key={index}
                                                            >
                                                                <span className="resume-timeline-dot" />

                                                                <div className="resume-experience-card">

                                                                    <h4>
                                                                        {safeString(
                                                                            exp?.role ||
                                                                            exp?.designation
                                                                        )}
                                                                    </h4>

                                                                    <strong>
                                                                        {safeString(
                                                                            exp?.company ||
                                                                            exp?.organization
                                                                        )}
                                                                    </strong>

                                                                    {exp?.duration && (
                                                                        <span>
                                                                            {safeString(exp.duration)}
                                                                        </span>
                                                                    )}

                                                                    {exp?.description && (
                                                                        <p>
                                                                            {safeString(exp.description)}
                                                                        </p>
                                                                    )}

                                                                </div>

                                                            </div>

                                                        )
                                                    )}

                                                </div>

                                            ) : (

                                                <p className="resume-empty-section">
                                                    No work experience has
                                                    been added yet.
                                                </p>

                                            )}

                                        </div>

                                        <div className="resume-section">

                                            <div className="resume-section-title">
                                                <span>03</span>
                                                <h3>Education</h3>
                                            </div>

                                            {resumeEducation.length > 0 ? (

                                                <div className="resume-education-list">

                                                    {resumeEducation.map(
                                                        (edu, index) => (

                                                            <div
                                                                className="resume-education-card"
                                                                key={index}
                                                            >

                                                                <div className="resume-education-icon">
                                                                    <GraduationCap size={19} />
                                                                </div>

                                                                <div>

                                                                    <h4>
                                                                        {safeString(
                                                                            edu?.degree ||
                                                                            edu?.qualification
                                                                        )}
                                                                    </h4>

                                                                    <strong>
                                                                        {safeString(
                                                                            edu?.institution ||
                                                                            edu?.university
                                                                        )}
                                                                    </strong>

                                                                    {edu?.year && (
                                                                        <span>
                                                                            {safeString(edu.year)}
                                                                        </span>
                                                                    )}

                                                                </div>

                                                            </div>

                                                        )
                                                    )}

                                                </div>

                                            ) : (

                                                <p className="resume-empty-section">
                                                    No education details
                                                    have been added yet.
                                                </p>

                                            )}

                                        </div>

                                    </div>

                                    <div className="resume-side-column">

                                        <div className="resume-side-card">

                                            <h3>Quick Facts</h3>

                                            <div className="resume-fact">
                                                <span>Experience</span>
                                                <strong>
                                                    {getExperience(selectedTrainer) ||
                                                        "Not specified"}
                                                </strong>
                                            </div>

                                            <div className="resume-fact">
                                                <span>Location</span>
                                                <strong>
                                                    {getLocation(selectedTrainer)}
                                                </strong>
                                            </div>

                                            <div className="resume-fact">
                                                <span>Commercial Rate</span>
                                                <strong>
                                                    {getTrainerRate(selectedTrainer) > 0
                                                        ? `₹${getTrainerRate(
                                                              selectedTrainer
                                                          )} / day`
                                                        : "On request"}
                                                </strong>
                                            </div>

                                            <div className="resume-fact">
                                                <span>Status</span>
                                                <strong>
                                                    {selectedTrainer?.status ===
                                                    "APPROVED"
                                                        ? "Verified"
                                                        : "Pending Review"}
                                                </strong>
                                            </div>

                                        </div>

                                        <div className="resume-side-card">

                                            <h3>Skills</h3>

                                            {resumeSkills.length > 0 ? (

                                                <div className="resume-skills-large">
                                                    {resumeSkills.map((skill) => (
                                                        <span key={skill}>
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>

                                            ) : (

                                                <p className="resume-side-text">
                                                    No skills listed yet.
                                                </p>

                                            )}

                                        </div>

                                    </div>

                                </div>

                                <div className="resume-footer">

                                    <span>
                                        Interested in this trainer?
                                        Download the full resume.
                                    </span>

                                    <button
                                        onClick={() =>
                                            downloadResume(selectedTrainer)
                                        }
                                    >
                                        <Download size={15} />
                                        Download Resume
                                    </button>

                                </div>

                            </div>

                        )}

                    </div>

                </div>

            )}

        </main>

    );

}


export default Home;